import React, { useEffect, useMemo, useState } from "react";
import { useRef } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { addDoc, collection, doc, getDocs, orderBy, query, Timestamp, updateDoc } from "firebase/firestore";
import { useHistory, useLocation } from "react-router-dom";
import Navbar from "../Components/Navbar/navbar";
import HistoricoCadastro from "../Components/HistoricoCadastro/HistoricoCadastro";
import { auth, db } from "../Config/firebase";
import { aplicarTenant, getUsuarioAtualComTenant, tenantWhere , aplicarTenantAtual} from "../Utils/tenant";
import { gerarSequencial } from "../Utils/sequencial";
import { atualizarDocumentoComMesclagem } from "../Utils/concorrenciaFirestore";
import {
  ENSAIOS_LABORATORIO,
  STATUS_ENSAIO_SOLICITADO,
  formatarDataBR,
  labelMaterial,
  normalizarTexto,
} from "./solicitacaoServicosModel";
import "./CadastroSolicitacaoServicos.css";

const ENSAIOS_GRUPO_IDS = new Set(
  ENSAIOS_LABORATORIO.filter((ensaio) => ensaio.subEnsaios?.length).map((ensaio) => ensaio.id)
);

const estadoInicial = {
  data: new Date().toISOString().slice(0, 10),
  clienteId: "",
  cliente: "",
  obraId: "",
  obra: "",
  materialId: "",
  material: "",
  observacoes: "",
  ensaios: [],
};

function ensaioInicial(ensaio) {
  return {
    id: ensaio.id,
    nome: ensaio.nome,
    rota: ensaio.rota || "",
    categoria: ensaio.categoria || "",
    status: "pendente",
  };
}

function textoMaterial(material = {}) {
  return normalizarTexto(
    [
      material.Descricao,
      material.descricao,
      material.Nome,
      material.nome,
      material.Fornecedor,
      material.fornecedor,
    ].filter(Boolean).join(" ")
  );
}

function materialPossuiTipoCadastrado(material = {}) {
  return [
    material.IsCimento,
    material.IsAditivo,
    material.IsAdicao,
    material.IsAgua,
    material.IsAgregadoGraudo,
    material.IsAgregadoMiudo,
    material.IsSolos,
  ].some((valor) => valor === true);
}

function materialEhCimento(material = {}) {
  if (materialPossuiTipoCadastrado(material)) return material.IsCimento === true;
  return material.IsCimento === true || /\bcimento\b/.test(textoMaterial(material));
}

function materialEhSolos(material = {}) {
  if (materialPossuiTipoCadastrado(material)) return material.IsSolos === true;
  return material.IsSolos === true || /\b(solo|solos)\b/.test(textoMaterial(material));
}

function materialEhAgregadoMiudo(material = {}) {
  if (materialPossuiTipoCadastrado(material)) return material.IsAgregadoMiudo === true;
  const texto = textoMaterial(material);
  return material.IsAgregadoMiudo === true || /\b(areia|miudo|po de pedra)\b/.test(texto);
}

function materialEhAgregadoGraudo(material = {}) {
  if (materialPossuiTipoCadastrado(material)) return material.IsAgregadoGraudo === true;
  const texto = textoMaterial(material);
  return material.IsAgregadoGraudo === true || /\b(brita|graudo|pedrisco)\b/.test(texto);
}

function materialEhAgregado(material = {}) {
  return materialEhAgregadoGraudo(material) || materialEhAgregadoMiudo(material);
}

function materialEhBritaZero(material = {}) {
  const classificacao = normalizarTexto(
    material.DescricaoAgregado ||
    material.descricaoAgregado ||
    material.Descricao ||
    material.descricao ||
    ""
  );
  return /^brita\s*0\b/.test(classificacao);
}

function tipoAgregadoMaterial(material = {}) {
  return materialEhAgregadoMiudo(material) ? "miudo" : "graudo";
}

function materialEhAdicao(material = {}) {
  if (materialPossuiTipoCadastrado(material)) {
    return material.IsAdicao === true || material.IsAditivo === true;
  }
  const texto = textoMaterial(material);
  return (
    material.IsAdicao === true ||
    material.IsAditivo === true ||
    /\b(adicao|aditivo|pozolana|metacaulim|silica)\b/.test(texto)
  );
}

function rotuloMaterialRAA(material = {}) {
  return [
    material.codigo || material.Codigo,
    material.Cliente || material.cliente,
    material.Fornecedor || material.fornecedor,
    material.Descricao || material.descricao || material.nome || material.Nome,
  ].filter(Boolean).join(" - ");
}

function ehEnsaioRAA(ensaio = {}) {
  return ensaio.tipo === "raa" || ensaio.id === "raa" || String(ensaio.id || "").startsWith("raa-");
}

function novoIdRAA() {
  return `raa-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function ensaioRAAInicial() {
  const ensaioRAA = ENSAIOS_LABORATORIO.find((ensaio) => ensaio.id === "raa");

  return {
    id: novoIdRAA(),
    tipo: "raa",
    nome: ensaioRAA?.nome || "Ensaio RAA",
    rota: ensaioRAA?.rota || "/app/ensaio-raa",
    categoria: ensaioRAA?.categoria || "Concreto",
    status: "pendente",
    cimento: "",
    cimentoId: "",
    adicao: "",
    adicaoId: "",
    percentualAdicao: "",
  };
}

function dadosCimentoRAA(ensaio = {}, materiais = []) {
  const materialCimento = materiais.find((material) => material.id === ensaio.cimentoId);

  return {
    codigo: materialCimento?.codigo || materialCimento?.Codigo || ensaio.cimentoCodigo || "",
    descricao:
      materialCimento?.Descricao ||
      materialCimento?.descricao ||
      materialCimento?.nome ||
      materialCimento?.Nome ||
      ensaio.cimento ||
      "",
    fornecedor:
      materialCimento?.Fornecedor ||
      materialCimento?.fornecedor ||
      ensaio.cimentoFornecedor ||
      "",
  };
}

function resumoCimentoRAA(ensaio = {}, materiais = []) {
  const cimento = dadosCimentoRAA(ensaio, materiais);
  const texto = [cimento.codigo, cimento.descricao, cimento.fornecedor].filter(Boolean).join(" - ");
  return texto ? `Cimento: ${texto}` : "";
}

function resumoComplementosRAA(ensaio = {}, materiais = []) {
  if (!ehEnsaioRAA(ensaio)) return "";

  return [
    resumoCimentoRAA(ensaio, materiais),
    ensaio.adicao && `Adição: ${ensaio.adicao}${ensaio.percentualAdicao ? ` (${ensaio.percentualAdicao}%)` : ""}`,
  ].filter(Boolean).join(" | ");
}

function usuarioAtualResumo() {
  const user = auth.currentUser;
  return {
    uid: user?.uid || "",
    nome: user?.displayName || user?.email || "UsuÃ¡rio nÃ£o informado",
    email: user?.email || "",
  };
}

function statusSolicitacao(ensaios = []) {
  if (!ensaios.length) return "Sem ensaios";
  if (ensaios.every((ensaio) => ensaio.status === "cancelado")) return "Cancelada";
  if (ensaios.every((ensaio) => ["concluido", "cancelado"].includes(ensaio.status))) return "Concluída";
  return "Pendente";
}

function corStatus(status) {
  if (status === "Concluída" || status === "concluido") return "success";
  if (status === "Cancelada" || status === "cancelado") return "error";
  return "warning";
}

function valorPreenchido(valor) {
  return valor !== undefined && valor !== null && String(valor).trim() !== "";
}

function ensaiosVisiveis(ensaios = []) {
  return ensaios.filter((ensaio) => !ENSAIOS_GRUPO_IDS.has(ensaio.id));
}

function ensaioEhConsistenciaCimento(ensaio = {}) {
  return ensaio.id === "cimentoConsistenciaPega";
}

function ensaioPermitidoParaMaterial(ensaio, material) {
  if (!material) return true;

  if (ensaioEhConsistenciaCimento(ensaio)) {
    return materialEhCimento(material);
  }

  if (ehEnsaioRAA(ensaio)) {
    return materialEhAgregado(material);
  }

  if (ensaio.id === "caracterizacaoAgregados") {
    return materialEhAgregado(material) || material.IsAdicao === true;
  }

  if (ensaio.id === "compactacao" || ensaio.id === "solo-cimento") {
    return materialEhSolos(material);
  }

  if (ensaio.id === "granulometria-asfalto") {
    return materialEhAgregado(material);
  }

  return true;
}

function subEnsaioPermitidoParaMaterial(ensaio, material) {
  if (!material) return true;

  if (String(ensaio.id || "").startsWith("caracterizacao-")) {
    const densidadeAbsorcaoAdicao =
      ensaio.id === "caracterizacao-densidade-absorcao" && material.IsAdicao === true;

    if (!materialEhAgregado(material) && !densidadeAbsorcaoAdicao) return false;

    if (ensaio.id === "caracterizacao-equivalente-areia") {
      return materialEhAgregadoMiudo(material);
    }

    if (ensaio.id === "caracterizacao-abrasao-los-angeles") {
      return materialEhAgregadoGraudo(material);
    }

    if (ensaio.id === "caracterizacao-indice-forma") {
      return materialEhAgregadoGraudo(material) && !materialEhBritaZero(material);
    }

    if (ensaio.id === "caracterizacao-impurezas-organicas") {
      return !materialEhBritaZero(material);
    }
  }

  if (String(ensaio.id || "").startsWith("compactacao-")) {
    return materialEhSolos(material);
  }

  return true;
}

function rotuloSubEnsaioParaMaterial(ensaio, material) {
  if (subEnsaioPermitidoParaMaterial(ensaio, material)) return ensaio.nome;

  if (ensaio.id === "caracterizacao-equivalente-areia" && materialEhAgregadoGraudo(material)) {
    return `${ensaio.nome} (somente agregado miúdo)`;
  }

  if (
    ["caracterizacao-indice-forma", "caracterizacao-impurezas-organicas"].includes(ensaio.id) &&
    materialEhBritaZero(material)
  ) {
    return `${ensaio.nome} (não aplicável à BRITA 0)`;
  }

  if (
    materialEhAgregadoMiudo(material) &&
    ["caracterizacao-abrasao-los-angeles", "caracterizacao-indice-forma"].includes(ensaio.id)
  ) {
    return `${ensaio.nome} (somente agregado graúdo)`;
  }

  return ensaio.nome;
}

function statusEnsaio(ensaio) {
  return ensaio.status || "pendente";
}

function resumoAndamentoEnsaios(ensaios = []) {
  const ensaiosAtivos = ensaios.filter(
    (ensaio) => statusEnsaio(ensaio) !== "cancelado"
  );
  const total = ensaiosAtivos.length;
  const faltantes = ensaiosAtivos.filter(
    (ensaio) => statusEnsaio(ensaio) === "pendente"
  ).length;

  return `${faltantes} de ${total}`;
}

function ordenarPendentesPrimeiro(ensaios = []) {
  return [...ensaios].sort((a, b) => {
    const aRAA = ehEnsaioRAA(a);
    const bRAA = ehEnsaioRAA(b);

    // Mantém todos os ensaios RAA agrupados no topo da visualização.
    if (aRAA !== bRAA) return aRAA ? -1 : 1;

    const aPendente = statusEnsaio(a) === "pendente";
    const bPendente = statusEnsaio(b) === "pendente";
    if (aPendente === bPendente) return 0;
    return aPendente ? -1 : 1;
  });
}

function corTextoStatusEnsaio(ensaio) {
  const status = statusEnsaio(ensaio);
  if (status === "concluido" || status === "em_andamento") return "success.main";
  if (status === "cancelado") return "error.main";
  return "text.primary";
}

function dataParaMillis(valor) {
  if (!valor) return null;
  if (typeof valor.toMillis === "function") return valor.toMillis();
  if (typeof valor.toDate === "function") return valor.toDate().getTime();
  if (valor instanceof Date) return valor.getTime();
  if (typeof valor === "string") {
    const data = new Date(valor);
    return Number.isNaN(data.getTime()) ? null : data.getTime();
  }
  if (valor.seconds) return valor.seconds * 1000;
  return null;
}

function caracterizacaoCriadaAposSolicitacao(caracterizacao, solicitacao) {
  const criadaEm = dataParaMillis(caracterizacao.criadoEm || caracterizacao.dataCadastro);
  const solicitadaEm = dataParaMillis(solicitacao.criadoEm || solicitacao.data);

  if (!criadaEm || !solicitadaEm) return false;
  return criadaEm >= solicitadaEm;
}

function raaCriadoAposSolicitacao(raa, solicitacao) {
  const criadoEm = dataParaMillis(raa.criadoEm || raa.dataMoldagem);
  const solicitadaEm = dataParaMillis(solicitacao.criadoEm || solicitacao.data);

  if (!criadoEm || !solicitadaEm) return false;
  return criadoEm >= solicitadaEm;
}

function caracterizacaoConcluiEnsaio(caracterizacao, ensaioId) {
  const testes = {
    "caracterizacao-granulometria": () => valorPreenchido(caracterizacao.dataGranulometria),
    "caracterizacao-densidade-absorcao": () => valorPreenchido(caracterizacao.dataDensidade),
    "caracterizacao-massa-unitaria": () => valorPreenchido(caracterizacao.dataMassaUnitaria),
    "caracterizacao-torroes-argila": () => valorPreenchido(caracterizacao.dataTorroesArgila),
    "caracterizacao-materiais-pulverulentos": () => valorPreenchido(caracterizacao.dataMateriaisPulverulentos),
    "caracterizacao-impurezas-organicas": () => valorPreenchido(caracterizacao.impurezasOrganicas?.data),
    "caracterizacao-equivalente-areia": () => valorPreenchido(caracterizacao.equivalenteAreia?.data),
    "caracterizacao-particulas-leves": () => valorPreenchido(caracterizacao.particulasLeves?.data),
    "caracterizacao-sais-cloretos-sulfatos": () => valorPreenchido(caracterizacao.saisCloretosSulfatos?.data),
    "caracterizacao-abrasao-los-angeles": () => valorPreenchido(caracterizacao.dataAbrasaoLosAngeles),
    "caracterizacao-indice-forma": () => valorPreenchido(caracterizacao.indiceForma?.data),
  };

  return Boolean(testes[ensaioId]?.());
}

function ehCaracterizacaoMiudo(caracterizacao) {
  const descricao = normalizarTexto(
    `${caracterizacao?.amostra || ""} ${caracterizacao?.descricao || ""} ${caracterizacao?.tipoMaterial || ""}`
  );

  if (/\b(areia|miudo|po de pedra)\b/.test(descricao)) return true;
  if (/\b(brita|graudo|pedrisco)\b/.test(descricao)) return false;
  return normalizarTexto(caracterizacao?.tipoAgregado) === "miudo";
}

function rotaDetalheCaracterizacao(caracterizacao) {
  const base = ehCaracterizacaoMiudo(caracterizacao)
    ? "/app/cadastro-caracterizacao-miudo"
    : "/app/cadastro-caracterizacao";
  return `${base}/${caracterizacao.id}`;
}

function idsIguais(valorA, valorB) {
  return String(valorA || "").trim() === String(valorB || "").trim();
}

function raaConcluiEnsaio(raa, solicitacao, ensaioSolicitado) {
  if (!raa || !solicitacao || !ensaioSolicitado) return false;
  if (raa.cancelado === true) return false;
  if (!raaCriadoAposSolicitacao(raa, solicitacao)) return false;

  const mesmoAgregado =
    idsIguais(raa.agregadoId, solicitacao.materialId) ||
    idsIguais(raa.fornecedorId, solicitacao.materialId);

  if (!mesmoAgregado) return false;

  const mesmoCimento = ensaioSolicitado.cimentoId
    ? idsIguais(raa.cimentoId, ensaioSolicitado.cimentoId)
    : true;

  const mesmaAdicao = ensaioSolicitado.adicaoId
    ? idsIguais(raa.adicaoId, ensaioSolicitado.adicaoId)
    : true;

  return mesmoCimento && mesmaAdicao;
}

function aplicarConclusoesAutomaticas(solicitacao, caracterizacoes, ensaiosRAA = []) {
  const caracterizacoesDoMaterial = caracterizacoes.filter(
    (caracterizacao) =>
      caracterizacao.fornecedorId === solicitacao.materialId &&
      caracterizacaoCriadaAposSolicitacao(caracterizacao, solicitacao)
  );

  let alterou = false;

  const ensaios = (solicitacao.ensaios || []).map((ensaio) => {
    if (ehEnsaioRAA(ensaio) && ensaio.raaId) {
      const raaVinculado = ensaiosRAA.find((raa) => raa.id === ensaio.raaId);
      if (raaVinculado?.cancelado === true && ensaio.status !== "cancelado") {
        alterou = true;
        return { ...ensaio, tipo: "raa", status: "cancelado" };
      }
    }

    if (ensaio.status === "cancelado") {
      return ensaio;
    }

    if (String(ensaio.id || "").startsWith("caracterizacao-")) {
  const concluidoPorCaracterizacao = caracterizacoesDoMaterial.some((caracterizacao) =>
    caracterizacaoConcluiEnsaio(caracterizacao, ensaio.id)
  );

  // Se encontrou caracterização feita, dá baixa automática.
  if (concluidoPorCaracterizacao && ensaio.status !== "concluido") {
    alterou = true;
    return { ...ensaio, status: "concluido" };
  }

  // Não volta automaticamente para pendente.
  // Se o usuário marcou manualmente como concluído, mantém concluído.
  return ensaio;
}

   if (ehEnsaioRAA(ensaio)) {
  const temComplementoRAA = Boolean(ensaio.cimentoId || ensaio.adicaoId);

  // RAA antigo/salvo sem cimento ou adição não deve ser baixado automaticamente
  // porque não dá para saber qual ensaio RAA ele representa.
  if (!temComplementoRAA) {
    return ensaio;
  }

  const raaEncontrado = ensaiosRAA.find((raa) =>
    raaConcluiEnsaio(raa, solicitacao, ensaio)
  );

  if (raaEncontrado && ensaio.status !== "concluido") {
    alterou = true;
    return {
      ...ensaio,
      tipo: "raa",
      status: "concluido",
      raaId: raaEncontrado.id,
      raaCodigo: raaEncontrado.codigo || "",
    };
  }

  return {
    ...ensaio,
    tipo: "raa",
  };
}

    return ensaio;
  });

  return alterou ? { ...solicitacao, ensaios, atualizarStatusAutomatico: true } : solicitacao;
}

function formatarNumeroOS(numero) {
  return String(numero || 0).padStart(4, "0");
}

export default function CadastroSolicitacaoServicos() {
  const history = useHistory();
  const location = useLocation();
  const aberturaCaracterizacaoProcessadaRef = useRef(false);
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [obras, setObras] = useState([]);
  const [materiais, setMateriais] = useState([]);
  const [caracterizacoes, setCaracterizacoes] = useState([]);
  const [ensaiosRAA, setEnsaiosRAA] = useState([]);
  const [form, setForm] = useState(estadoInicial);
  const [busca, setBusca] = useState("");
  const [buscaCliente, setBuscaCliente] = useState("");
  const [filtroStatusLista, setFiltroStatusLista] = useState("todas");
  const [openDialog, setOpenDialog] = useState(false);
  const [resumoPendenciasAberto, setResumoPendenciasAberto] = useState(false);
  const [solicitacaoAbertaId, setSolicitacaoAbertaId] = useState("");
  const [filtrosEnsaiosSolicitacao, setFiltrosEnsaiosSolicitacao] = useState({});
  const [solicitacaoEditandoId, setSolicitacaoEditandoId] = useState("");
  const [ensaiosOriginaisIds, setEnsaiosOriginaisIds] = useState([]);
  const [novoEnsaio, setNovoEnsaio] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [usuarioAtual, setUsuarioAtual] = useState(null);
  const [mostrarTodosMateriais, setMostrarTodosMateriais] = useState(false);


  useEffect(() => {
    carregarDados();
  }, []);

  useEffect(() => {
    if (aberturaCaracterizacaoProcessadaRef.current || !location.state?.origemCaracterizacao) return;

    aberturaCaracterizacaoProcessadaRef.current = true;
    abrirCadastro();
    history.replace(location.pathname);
  }, [history, location.pathname, location.state]);

  async function carregarDados() {
    setCarregandoLista(true);
    try {
      const usuario = await getUsuarioAtualComTenant();
      setUsuarioAtual(usuario);

    const [
  solicitacoesSnap,
  clientesSnap,
  obrasSnap,
  materiaisSnap,
  caracterizacoesSnap,
  ensaiosRAASnap,
] = await Promise.all([
  getDocs(query(collection(db, "tblSolicitacoesServicos"), ...tenantWhere(usuario), orderBy("criadoEm", "desc"))),
  getDocs(query(collection(db, "clientes"), ...tenantWhere(usuario), orderBy("nome"))),
  getDocs(query(collection(db, "obras"), ...tenantWhere(usuario), orderBy("nome"))),
  getDocs(query(collection(db, "tblListaMateriaisCadastrados"), ...tenantWhere(usuario), orderBy("codigo", "desc"))),
  getDocs(query(collection(db, "tblCaracterizacaoAgregados"), ...tenantWhere(usuario))),
  getDocs(query(collection(db, "tblEnsaioRAA"), ...tenantWhere(usuario))),
]);

    const caracterizacoes = caracterizacoesSnap.docs.map((item) => ({ id: item.id, ...item.data() }));
const ensaiosRAA = ensaiosRAASnap.docs.map((item) => ({ id: item.id, ...item.data() }));

const solicitacoesAtualizadas = solicitacoesSnap.docs.map((item) =>
  aplicarConclusoesAutomaticas({ id: item.id, ...item.data() }, caracterizacoes, ensaiosRAA)
);

    setCaracterizacoes(caracterizacoes);
    setEnsaiosRAA(ensaiosRAA);
    setSolicitacoes(solicitacoesAtualizadas);
    setClientes(clientesSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
    setObras(obrasSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
    setMateriais(materiaisSnap.docs.map((item) => ({ id: item.id, ...item.data() })));

      await Promise.all(
      solicitacoesAtualizadas
        .filter((solicitacao) => solicitacao.atualizarStatusAutomatico)
        .map(({ id, ensaios }) =>
          updateDoc(
            doc(db, "tblSolicitacoesServicos", id),
            aplicarTenant({ ensaios }, usuario)
          )
        )
      );
    } finally {
      setCarregandoLista(false);
    }
  }

  const clienteSelecionado = clientes.find((cliente) => cliente.id === form.clienteId) || null;
  const obrasFiltradas = form.clienteId
    ? obras.filter((obra) => obra.clienteId === form.clienteId)
    : obras;
  const obraSelecionada = obras.find((obra) => obra.id === form.obraId) || null;
  const materiaisFiltrados = mostrarTodosMateriais
    ? materiais
    : materiais.filter((material) => {
        if (form.obraId) return material.ObraId === form.obraId;
        if (form.clienteId) return material.ClienteId === form.clienteId;
        return true;
      });
  const materialSelecionado = materiais.find((material) => material.id === form.materialId) || null;
  const dadosBasicosPreenchidos = Boolean(
    form.clienteId && form.obraId && form.materialId
  );
  
  
  const ensaiosLaboratorioDisponiveis = useMemo(() => {
  return ENSAIOS_LABORATORIO.filter((ensaio) => {
    const jaSalvo =
      ensaioJaSalvo(ensaio.id) ||
      (ensaio.subEnsaios || []).some((subEnsaio) =>
        ensaioJaSalvo(subEnsaio.id)
      );

    if (jaSalvo) return true;

    return ensaioPermitidoParaMaterial(ensaio, materialSelecionado);
  });
}, [materialSelecionado, ensaiosOriginaisIds]);


  
// Cimento do RAA deve buscar somente materiais marcados como cimento.
// Não pode cair para agregado da obra.
const materiaisCimentoRAA = materiais.filter(materialEhCimento);

// Adição/aditivo também deve buscar somente materiais marcados como adição/aditivo.
const materiaisAdicaoRAA = materiais.filter(materialEhAdicao);

  const listaFiltrada = useMemo(() => {
  const termo = normalizarTexto(busca);

  return solicitacoes.filter((solicitacao) => {
    const ensaiosDaSolicitacao = ensaiosVisiveis(solicitacao.ensaios);
    const statusGeral = statusSolicitacao(ensaiosDaSolicitacao);
    const numeroOS = solicitacao.numeroOS || solicitacao.numeroOs || solicitacao.numero || "";
    const numeroOSFormatado =
      solicitacao.numeroOSFormatado || (numeroOS ? formatarNumeroOS(numeroOS) : "");

    const passaFiltroStatus =
      filtroStatusLista === "todas" ||
      (filtroStatusLista === "pendentes" && statusGeral === "Pendente");

    if (!passaFiltroStatus) return false;

    if (!termo) return true;

    return [
      numeroOS,
      numeroOSFormatado,
      numeroOSFormatado ? `OS ${numeroOSFormatado}` : "",
      numeroOSFormatado ? `O.S. ${numeroOSFormatado}` : "",
      solicitacao.material,
      solicitacao.cliente,
      solicitacao.obra,
      solicitacao.observacoes,
      statusGeral,
      ...(solicitacao.ensaios || []).map((ensaio) => ensaio.nome),
      ...(solicitacao.ensaios || []).map((ensaio) => resumoComplementosRAA(ensaio, materiais)),
    ].some((valor) => normalizarTexto(valor).includes(termo));
  });
}, [busca, solicitacoes, filtroStatusLista]);

  function abrirCadastro() {
    setForm(estadoInicial);
    setBuscaCliente("");
    setMostrarTodosMateriais(false);
    setSolicitacaoEditandoId("");
    setEnsaiosOriginaisIds([]);
    setNovoEnsaio("");
    setOpenDialog(true);
  }

  function abrirEdicaoSolicitacao(solicitacao) {
    const ensaios = ensaiosVisiveis(solicitacao.ensaios);

    setForm({
      ...estadoInicial,
      data: solicitacao.data || estadoInicial.data,
      clienteId: solicitacao.clienteId || "",
      cliente: solicitacao.cliente || "",
      obraId: solicitacao.obraId || "",
      obra: solicitacao.obra || "",
      materialId: solicitacao.materialId || "",
      material: solicitacao.material || "",
      observacoes: solicitacao.observacoes || "",
      ensaios,
    });
    setSolicitacaoEditandoId(solicitacao.id);
    setBuscaCliente(solicitacao.cliente || "");
    setEnsaiosOriginaisIds(ensaios.map((ensaio) => ensaio.id));
    setNovoEnsaio("");
    setOpenDialog(true);
  }

  function fecharDialog() {
    setOpenDialog(false);
    setBuscaCliente("");
    setSolicitacaoEditandoId("");
    setEnsaiosOriginaisIds([]);
    setNovoEnsaio("");
  }

  function ensaioJaSalvo(ensaioId) {
    return ensaiosOriginaisIds.includes(ensaioId);
  }

  function ensaioSalvoProtegido(ensaioId) {
    return ensaioJaSalvo(ensaioId) &&
      !(solicitacaoEditandoId && String(ensaioId || "").startsWith("caracterizacao-"));
  }

  function selecionarObra(obra) {
    const cliente = clientes.find((item) => item.id === obra?.clienteId) || null;

    setForm((atual) => ({
      ...atual,
      clienteId: cliente?.id || atual.clienteId,
      cliente: cliente?.nome || atual.cliente,
      obraId: obra?.id || "",
      obra: obra?.nome || "",
      materialId: "",
      material: "",
    }));
  }

 function filtrarEnsaiosSelecionadosPorMaterial(ensaios, material) {
  return (ensaios || []).filter((ensaio) => {
    if (ensaioJaSalvo(ensaio.id)) return true;

    if (
      String(ensaio.id || "").startsWith("caracterizacao-") ||
      String(ensaio.id || "").startsWith("compactacao-")
    ) {
      return subEnsaioPermitidoParaMaterial(ensaio, material);
    }

    return ensaioPermitidoParaMaterial(ensaio, material);
  });
}

  function selecionarMaterial(material) {
    const cliente = clientes.find((item) => item.id === material?.ClienteId) || null;
    const obra = obras.find((item) => item.id === material?.ObraId) || null;

    setForm((atual) => ({
      ...atual,
      clienteId: cliente?.id || material?.ClienteId || atual.clienteId,
      cliente: cliente?.nome || atual.cliente,
      obraId: obra?.id || material?.ObraId || atual.obraId,
      obra: obra?.nome || atual.obra,
      materialId: material?.id || "",
      material: material ? labelMaterial(material) : "",
      ensaios: filtrarEnsaiosSelecionadosPorMaterial(atual.ensaios || [], material),
    }));
  }

  function selecionarEnsaio(ensaio, marcado) {
    setForm((atual) => {
      const ensaiosAtuais = atual.ensaios || [];
      if (!marcado) {
        if (ensaioSalvoProtegido(ensaio.id)) return atual;
        return { ...atual, ensaios: ensaiosAtuais.filter((item) => item.id !== ensaio.id) };
      }

      if (ensaiosAtuais.some((item) => item.id === ensaio.id)) return atual;
      return { ...atual, ensaios: [...ensaiosAtuais, ensaioInicial(ensaio)] };
    });
  }

  function adicionarNovoRAA() {
  setForm((atual) => ({
    ...atual,
    ensaios: [...(atual.ensaios || []), ensaioRAAInicial()],
  }));
}

  function atualizarDadosRAA(ensaioRAAId, valor) {
  setForm((atual) => ({
    ...atual,
    ensaios: (atual.ensaios || []).map((ensaio) =>
      ensaio.id === ensaioRAAId && ehEnsaioRAA(ensaio)
        ? {
            ...ensaio,
            ...valor,
            ...(!valor.adicaoId && Object.prototype.hasOwnProperty.call(valor, "adicaoId")
              ? { percentualAdicao: "" }
              : {}),
          }
        : ensaio
    ),
  }));
}

  function selecionarTodosSubEnsaios(ensaios, marcado) {
    const ids = ensaios.map((ensaio) => ensaio.id);

    setForm((atual) => {
      const ensaiosAtuais = atual.ensaios || [];

      if (!marcado) {
        return {
          ...atual,
          ensaios: ensaiosAtuais.filter((ensaio) => !ids.includes(ensaio.id) || ensaioSalvoProtegido(ensaio.id)),
        };
      }

      const novosEnsaios = ensaios
        .filter((ensaio) => !ensaiosAtuais.some((item) => item.id === ensaio.id))
        .map(ensaioInicial);

      return {
        ...atual,
        ensaios: [...ensaiosAtuais, ...novosEnsaios],
      };
    });
  }

  function selecionarGrupoEnsaio(ensaio, marcado) {
    setForm((atual) => {
      const ensaiosAtuais = atual.ensaios || [];
      const subEnsaiosIds = (ensaio.subEnsaios || []).map((subEnsaio) => subEnsaio.id);

      if (!marcado) {
        return {
          ...atual,
          ensaios: ensaiosAtuais.filter(
            (item) =>
              ensaioSalvoProtegido(item.id) ||
              (item.id !== ensaio.id && !subEnsaiosIds.includes(item.id))
          ),
        };
      }

      if (ensaiosAtuais.some((item) => item.id === ensaio.id)) return atual;
      return { ...atual, ensaios: [...ensaiosAtuais, ensaioInicial(ensaio)] };
    });
  }

  function adicionarEnsaioAvulso() {
    const nome = novoEnsaio.trim();
    if (!nome) return;

    setForm((atual) => ({
      ...atual,
      ensaios: [
        ...(atual.ensaios || []),
        {
          id: `avulso-${Date.now()}`,
          nome,
          rota: "",
          categoria: "Avulso",
          status: "pendente",
        },
      ],
    }));
    setNovoEnsaio("");
  }

  function removerEnsaioForm(ensaioId) {
    if (ensaioSalvoProtegido(ensaioId)) return;

    setForm((atual) => ({
      ...atual,
      ensaios: (atual.ensaios || []).filter((ensaio) => ensaio.id !== ensaioId),
    }));
  }

  async function salvarSolicitacao() {
  if (!form.clienteId) {
    alert("Selecione o cliente.");
    return;
  }

  if (!form.obraId) {
    alert("Selecione a obra.");
    return;
  }

  if (!form.materialId) {
    alert("Selecione o material.");
    return;
  }

  let ensaiosSelecionados = ensaiosVisiveis(form.ensaios);

  if (!ensaiosSelecionados.length) {
    alert("Selecione pelo menos um ensaio.");
    return;
  }

  
  try {
    setSalvando(true);
    const usuario = usuarioAtual || await getUsuarioAtualComTenant();
    const solicitacaoReferencia =
      solicitacoes.find((item) => item.id === solicitacaoEditandoId) || {
        materialId: form.materialId,
        data: form.data,
        criadoEm: new Date(),
      };

    ensaiosSelecionados = ensaiosSelecionados.map((ensaio) => {
      if (!ehEnsaioRAA(ensaio)) return ensaio;
      if (ensaio.status === "cancelado") {
        return { ...ensaio, tipo: "raa" };
      }

      const raaEncontrado = ensaiosRAA.find((raa) =>
        raaConcluiEnsaio(
          raa,
          {
            ...solicitacaoReferencia,
            materialId: form.materialId,
          },
          ensaio
        )
      );

      if (!raaEncontrado) {
        return {
          ...ensaio,
          tipo: "raa",
        };
      }

      return {
        ...ensaio,
        tipo: "raa",
        status: "concluido",
        raaId: raaEncontrado.id,
        raaCodigo: raaEncontrado.codigo || "",
      };
    });

    const numeroOSAtual = solicitacaoEditandoId
      ? solicitacoes.find((item) => item.id === solicitacaoEditandoId)?.numeroOS
      : null;
    const sequencialOS = numeroOSAtual
      ? null
      : await gerarSequencial({
          chave: "os_interna",
          formato: { padding: 4 },
          valorMinimo: solicitacoes.reduce((maior, item) => {
            const numero = Number(item.numeroOS || item.numeroOs || item.numero || 0);
            return Number.isFinite(numero) && numero > maior ? numero : maior;
          }, 0),
        });
    const numeroOS = numeroOSAtual || sequencialOS.numero;

    const dadosSolicitacao = aplicarTenant({
      numeroOS,
      numeroOSFormatado: numeroOS ? formatarNumeroOS(numeroOS) : "",
      data: form.data,
      clienteId: form.clienteId,
      cliente: clienteSelecionado?.nome || form.cliente,
      obraId: form.obraId,
      obra: obraSelecionada?.nome || form.obra,
      materialId: form.materialId,
      material: materialSelecionado ? labelMaterial(materialSelecionado) : form.material,
      materialDados: materialSelecionado || {},
      ensaios: ensaiosSelecionados,
      observacoes: form.observacoes || "",
      atualizadoEm: Timestamp.now(),
      atualizadoPor: usuarioAtualResumo(),
    }, usuario);

    let solicitacaoSalvaId = solicitacaoEditandoId;
    const deveAbrirCaracterizacao = !solicitacaoEditandoId;

    if (solicitacaoEditandoId) {
      await atualizarDocumentoComMesclagem({
        db,
        referencia: doc(db, "tblSolicitacoesServicos", solicitacaoEditandoId),
        original: solicitacaoReferencia,
        atual: { ...solicitacaoReferencia, ...dadosSolicitacao },
        metadados: {
          atualizadoEm: dadosSolicitacao.atualizadoEm,
          atualizadoPor: dadosSolicitacao.atualizadoPor,
        },
      });
    } else {
      const solicitacaoRef = await addDoc(collection(db, "tblSolicitacoesServicos"), aplicarTenantAtual({
        ...dadosSolicitacao,
        criadoPor: usuarioAtualResumo(),
        criadoEm: Timestamp.now(),
      }));
      solicitacaoSalvaId = solicitacaoRef.id;
    }

    fecharDialog();
    await carregarDados();

    const possuiEnsaioCaracterizacao = ensaiosSelecionados.some((ensaio) =>
      String(ensaio.id || "").startsWith("caracterizacao-")
    );
    if (deveAbrirCaracterizacao && possuiEnsaioCaracterizacao && solicitacaoSalvaId) {
      const material = materialSelecionado || form.materialDados || {};
      const maiorCodigoCaracterizacao = caracterizacoes.reduce((maior, caracterizacao) => {
        const numero = Number(String(caracterizacao.codigo || "").match(/\d+/)?.[0] || 0);
        return Number.isFinite(numero) && numero > maior ? numero : maior;
      }, 0);
      const sequencialCaracterizacao = await gerarSequencial({
        chave: "caracterizacao_agregados",
        formato: { padding: 3 },
        valorMinimo: maiorCodigoCaracterizacao,
      });
      const codigoCaracterizacao =
        sequencialCaracterizacao.codigo || String(sequencialCaracterizacao.numero || "").padStart(3, "0");
      const tipoAgregado = tipoAgregadoMaterial(material);
      const caracterizacaoRef = await addDoc(
        collection(db, "tblCaracterizacaoAgregados"),
        aplicarTenantAtual({
          codigo: codigoCaracterizacao,
          tipoAgregado,
          solicitacaoId: solicitacaoSalvaId,
          fornecedorId: form.materialId || "",
          fornecedor: material.Fornecedor || material.fornecedor || "",
          codigoFornecedor: material.codigo || "",
          cliente: clienteSelecionado?.nome || form.cliente || "",
          obra: obraSelecionada?.nome || form.obra || "",
          obraId: form.obraId || "",
          amostra: material.Descricao || material.descricao || "",
          localColeta: material.LocalColeta || material.localColeta || "",
          procedencia: material.Fornecedor || material.fornecedor || "",
          dataCadastro: form.data || new Date().toISOString().slice(0, 10),
          criadoEm: Timestamp.now(),
          atualizadoEm: Timestamp.now(),
        })
      );
      const rotaDetalhes =
        tipoAgregado === "miudo"
          ? `/app/cadastro-caracterizacao-miudo/${caracterizacaoRef.id}`
          : `/app/cadastro-caracterizacao/${caracterizacaoRef.id}`;
      history.push(`${rotaDetalhes}?solicitacaoId=${encodeURIComponent(solicitacaoSalvaId)}`);
    }
  } catch (erro) {
    console.error("Erro ao salvar O.S. interna:", erro);
    alert("Erro ao salvar a O.S. interna. Verifique o console.");
  } finally {
    setSalvando(false);
  }
}

  async function alterarStatusEnsaio(solicitacao, ensaioId, status) {
  const usuario = usuarioAtual || await getUsuarioAtualComTenant();
  const ensaioAlterado = (solicitacao.ensaios || []).find(
    (ensaio) => ensaio.id === ensaioId
  );
  const ensaios = (solicitacao.ensaios || []).map((ensaio) =>
    ensaio.id === ensaioId ? { ...ensaio, status } : ensaio
  );
  const atualizadoEm = Timestamp.now();

  const atualizacoes = [
    updateDoc(doc(db, "tblSolicitacoesServicos", solicitacao.id), aplicarTenant({
      ensaios,
      atualizadoEm,
      atualizadoPor: usuarioAtualResumo(),
    }, usuario)),
  ];

  if (status === "cancelado" && ehEnsaioRAA(ensaioAlterado) && ensaioAlterado.raaId) {
    atualizacoes.push(
      updateDoc(doc(db, "tblEnsaioRAA", ensaioAlterado.raaId), {
        cancelado: true,
        atualizadoEm,
      })
    );
  }

  await Promise.all(atualizacoes);

  if (status === "cancelado" && ensaioAlterado?.raaId) {
    setEnsaiosRAA((atuais) =>
      atuais.map((raa) =>
        raa.id === ensaioAlterado.raaId ? { ...raa, cancelado: true, atualizadoEm } : raa
      )
    );
  }

  setSolicitacoes((atuais) =>
    atuais.map((item) =>
      item.id === solicitacao.id
        ? {
            ...item,
            ensaios,
            atualizadoEm,
            atualizadoPor: usuarioAtualResumo(),
          }
        : item
    )
  );
}

  function abrirRotaEnsaio(rota) {
    if (rota) history.push(rota);
  }

  function caracterizacaoConcluidaDaSolicitacao(solicitacao, ensaio) {
    if (!String(ensaio.id || "").startsWith("caracterizacao-")) return null;

    return caracterizacoes.find(
      (caracterizacao) =>
        caracterizacao.fornecedorId === solicitacao.materialId &&
        caracterizacaoCriadaAposSolicitacao(caracterizacao, solicitacao) &&
        caracterizacaoConcluiEnsaio(caracterizacao, ensaio.id)
    ) || null;
  }

  function caracterizacaoExistenteDaSolicitacao(solicitacao, ensaio) {
    if (!String(ensaio.id || "").startsWith("caracterizacao-")) return null;

    return caracterizacoes.find(
      (caracterizacao) =>
        caracterizacao.fornecedorId === solicitacao.materialId &&
        caracterizacaoCriadaAposSolicitacao(caracterizacao, solicitacao)
    ) || null;
  }

  function abrirEnsaioSolicitado(solicitacao, ensaio) {
    if (ehEnsaioRAA(ensaio) && ensaio.raaId) {
    history.push(`/app/ensaio-raa/${ensaio.raaId}`);
    return;
  }
    const caracterizacao = caracterizacaoExistenteDaSolicitacao(solicitacao, ensaio);
    if (caracterizacao) {
      history.push(`${rotaDetalheCaracterizacao(caracterizacao)}?solicitacaoId=${encodeURIComponent(solicitacao.id)}`);
      return;
    }

    if (String(ensaio.id || "").startsWith("caracterizacao-")) {
      const material =
        materiais.find((item) => item.id === solicitacao.materialId) ||
        solicitacao.materialDados ||
        {};
      const tipoAgregado = tipoAgregadoMaterial(material);
      history.push(
        `/app/cadastro-caracterizacao?novo=1&tipoAgregado=${tipoAgregado}&solicitacaoId=${encodeURIComponent(solicitacao.id)}&materialId=${encodeURIComponent(
          solicitacao.materialId || ""
        )}`
      );
      return;
    }

    abrirRotaEnsaio(ensaio.rota);
  }

  function abrirRelatorioSolicitacao(solicitacao) {
    const ensaiosSolicitados = ensaiosVisiveis(solicitacao.ensaios);
    const ensaioCaracterizacao = ensaiosSolicitados.find((ensaio) =>
      String(ensaio.id || "").startsWith("caracterizacao-")
    );
    const raaIds = ensaiosSolicitados
      .filter((ensaio) => ehEnsaioRAA(ensaio) && ensaio.raaId)
      .map((ensaio) => ensaio.raaId);
    const ensaiosCaracterizacaoIds = ensaiosSolicitados
      .filter((ensaio) => String(ensaio.id || "").startsWith("caracterizacao-"))
      .map((ensaio) => ensaio.id);
    const caracterizacao = ensaioCaracterizacao
      ? caracterizacaoExistenteDaSolicitacao(solicitacao, ensaioCaracterizacao)
      : null;

    if (!caracterizacao) {
      alert("Nenhuma caracterização de agregado foi cadastrada para esta O.S.");
      return;
    }

    history.push("/app/relatorios/caracterizacao", {
      materialId: caracterizacao.id,
      abrirPrevia: true,
      origemOS: true,
      raaIds,
      ensaiosCaracterizacaoIds,
    });
  }

  function alterarFiltroEnsaiosSolicitacao(solicitacaoId, filtro) {
    setFiltrosEnsaiosSolicitacao((atual) => ({
      ...atual,
      [solicitacaoId]: filtro,
    }));
  }

  function alternarMostrarTodos(solicitacaoId) {
    alterarFiltroEnsaiosSolicitacao(solicitacaoId, "todos");
  }

  const osComPendencias = solicitacoes
    .map((solicitacao) => ({
      solicitacao,
      ensaiosPendentes: ensaiosVisiveis(solicitacao.ensaios).filter(
        (ensaio) => statusEnsaio(ensaio) === "pendente"
      ),
    }))
    .filter((item) => item.ensaiosPendentes.length > 0);
  const totalEnsaiosPendentes = osComPendencias.reduce(
    (total, item) => total + item.ensaiosPendentes.length,
    0
  );

  return (
    <>
      <Navbar />
      <Box className="solicitacao-servicos-page">
        <Paper className="cabecalho-lista-cadastro" elevation={0}>
        <Box className="solicitacao-servicos-topo">
          <Box>
            <Typography variant="h5" fontWeight={800}>
              O.S. interna
            </Typography>
            <Typography color="text.secondary">
              Controle das ordens de serviço internas para cada material recebido.
            </Typography>
          </Box>

          <Button variant="contained" startIcon={<AddIcon />} onClick={abrirCadastro}>
            Cadastrar
          </Button>
        </Box>

       <Paper className="solicitacao-servicos-filtros" variant="outlined">
  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
    <TextField
      label="Buscar O.S. interna"
      value={busca}
      onChange={(event) => setBusca(event.target.value)}
      fullWidth
    />

    <TextField
      select
      label="Status"
      value={filtroStatusLista}
      onChange={(event) => setFiltroStatusLista(event.target.value)}
      sx={{ width: { xs: "100%", sm: 180 } }}
    >
      <MenuItem value="todas">Todas</MenuItem>
      <MenuItem value="pendentes">Pendentes</MenuItem>
    </TextField>

    <Button
      variant="outlined"
      color="warning"
      onClick={() => setResumoPendenciasAberto(true)}
      sx={{
        whiteSpace: "nowrap",
        fontSize: 11,
        px: 1,
        minWidth: 145,
      }}
    >
      {totalEnsaiosPendentes} ensaios pendentes
    </Button>
  </Stack>
</Paper>
        </Paper>

        <Paper className="solicitacao-servicos-lista" variant="outlined">
          <Table className="lista-cadastro-padrao" size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell className="solicitacao-col-data">O.S.</TableCell>
                <TableCell className="solicitacao-col-data">Data</TableCell>
                <TableCell className="solicitacao-col-cliente">Cliente / Obra</TableCell>
                <TableCell className="solicitacao-col-material">Material</TableCell>
                <TableCell className="solicitacao-col-ensaios">Ensaios</TableCell>
                <TableCell>Status</TableCell>
                <TableCell className="solicitacao-col-acoes" align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {listaFiltrada.map((solicitacao) => {
                const aberta = solicitacaoAbertaId === solicitacao.id;
                const ensaiosDaSolicitacao = ensaiosVisiveis(solicitacao.ensaios);
                const filtroEnsaios = filtrosEnsaiosSolicitacao[solicitacao.id] || "todos";
                const mostrarTodosEnsaios = true;
                const ensaiosOrdenados = ordenarPendentesPrimeiro(ensaiosDaSolicitacao);
                const ensaiosDetalhe =
                  filtroEnsaios === "todos"
                    ? ensaiosOrdenados
                    : ensaiosOrdenados.filter((ensaio) => statusEnsaio(ensaio) === filtroEnsaios);
                const statusGeral = statusSolicitacao(ensaiosDaSolicitacao);

                return (
                  <React.Fragment key={solicitacao.id}>
                    <TableRow hover>
                     <TableCell className="solicitacao-col-data lista-dado-secundario">
                       {solicitacao.numeroOSFormatado || (solicitacao.numeroOS ? formatarNumeroOS(solicitacao.numeroOS) : "-")}
                      </TableCell>
                      <TableCell className="solicitacao-col-data lista-dado-secundario">
                        {formatarDataBR(solicitacao.data)}
                      </TableCell>
                      <TableCell className="solicitacao-col-cliente">
                        <div className="lista-dado-principal">{solicitacao.cliente || "-"}</div>
                        <div className="lista-dado-secundario">Obra: {solicitacao.obra || "-"}</div>
                      </TableCell>
                      <TableCell className="solicitacao-col-material lista-dado-destaque">{solicitacao.material || "-"}</TableCell>
                    
                        <TableCell className="solicitacao-col-ensaios">
                          {statusGeral === "Pendente" && (
                            <Typography variant="caption" color="text.secondary">
                              pendente
                            </Typography>
                          )}
                          <Typography fontWeight={700}>
                            {statusGeral === "Concluída"
                              ? ensaiosDaSolicitacao.filter(
                                  (ensaio) => statusEnsaio(ensaio) !== "cancelado"
                                ).length
                              : resumoAndamentoEnsaios(ensaiosDaSolicitacao)}
                          </Typography>
                        </TableCell>
                      <TableCell>
                        <Chip size="small" color={corStatus(statusGeral)} label={statusGeral} />
                      </TableCell>
                      <TableCell className="solicitacao-col-acoes" align="center">
                        <Tooltip title="Ver ensaios solicitados">
                          <IconButton
                            onClick={() => {
                              setSolicitacaoAbertaId(aberta ? "" : solicitacao.id);
                              if (!aberta) {
                                setFiltrosEnsaiosSolicitacao((atual) => ({
                                  ...atual,
                                  [solicitacao.id]: "todos",
                                }));
                              }
                            }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar O.S. interna">
                          <IconButton onClick={() => abrirEdicaoSolicitacao(solicitacao)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Gerar relatório">
                          <IconButton onClick={() => abrirRelatorioSolicitacao(solicitacao)}>
                            <AssignmentIcon color="primary" />
                          </IconButton>
                        </Tooltip>
                        <HistoricoCadastro colecao="tblSolicitacoesServicos" documentoId={solicitacao.id} registro={solicitacao} />
                      </TableCell>
                    </TableRow>

                    {aberta && (
                      <TableRow>
                        <TableCell colSpan={7} className="solicitacao-servicos-detalhe">
                          <Stack spacing={1.2}>
                            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                              <Typography variant="body2" color="text.secondary">
                                Mostrando {filtroEnsaios === "todos" ? "todos" : STATUS_ENSAIO_SOLICITADO[filtroEnsaios]} ({ensaiosDetalhe.length})
                              </Typography>

                              {false && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => alternarMostrarTodos(solicitacao.id)}
                                >
                                  {mostrarTodosEnsaios ? "Mostrar sÃ³ pendentes" : "Mostrar todos"}
                                </Button>
                              )}
                            </Stack>

                            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                              <TextField
                                select
                                size="small"
                                label="Filtrar ações"
                                value={filtroEnsaios}
                                onChange={(event) => alterarFiltroEnsaiosSolicitacao(solicitacao.id, event.target.value)}
                                sx={{ width: 180 }}
                              >
                                <MenuItem value="todos">Todos</MenuItem>
                                {Object.entries(STATUS_ENSAIO_SOLICITADO).map(([valor, label]) => (
                                  <MenuItem key={valor} value={valor}>{label}</MenuItem>
                                ))}
                              </TextField>
                            </Stack>

                            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                              <Typography variant="caption" color="text.secondary">
                                Criada por {solicitacao.criadoPor?.nome || "Usuário não informado"} em {formatarDataBR(solicitacao.criadoEm || solicitacao.data)}
                              </Typography>
                            </Stack>

                            {ensaiosDetalhe.length === 0 && (
                              <Typography color="text.secondary">
                                Nenhum ensaio encontrado para este filtro.
                              </Typography>
                            )}

                            {ensaiosDetalhe.map((ensaio) => (
                              <Box key={ensaio.id} className="solicitacao-servicos-ensaio">
                               <Stack spacing={0.3} alignItems="flex-start" sx={{ minWidth: 0 }}>
  {ensaio.rota ? (
    <Link
      component="button"
      onClick={() => abrirEnsaioSolicitado(solicitacao, ensaio)}
      sx={{
        color: corTextoStatusEnsaio(ensaio),
        fontWeight: 700,
        textAlign: "left",
        p: 0,
        minWidth: 0,
        display: "inline-block",
      }}
    >
      {ensaio.nome}
    </Link>
  ) : (
    <Typography fontWeight={700} color={corTextoStatusEnsaio(ensaio)}>
      {ensaio.nome}
    </Typography>
  )}

  <Typography variant="caption" color="text.secondary" display="block">
    {ensaio.categoria || "Ensaio"}
  </Typography>

  {ehEnsaioRAA(ensaio) && resumoComplementosRAA(ensaio, materiais) && (
    <Typography variant="caption" color="text.secondary" display="block">
      {resumoComplementosRAA(ensaio, materiais)}
    </Typography>
  )}
</Stack>

                                <TextField
                                  select
                                  size="small"
                                  label="Ações"
                                  value={ensaio.status || "pendente"}
                                  onChange={(event) =>
                                    alterarStatusEnsaio(solicitacao, ensaio.id, event.target.value)
                                  }
                                  sx={{ width: 170 }}
                                >
                                  {Object.entries(STATUS_ENSAIO_SOLICITADO).map(([valor, label]) => (
                                    <MenuItem key={valor} value={valor}>{label}</MenuItem>
                                  ))}
                                </TextField>
                              </Box>
                            ))}

                            {solicitacao.observacoes && (
                              <Typography color="text.secondary">{solicitacao.observacoes}</Typography>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}

              {carregandoLista && <TableRow><TableCell colSpan={7}><Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5, py: 4 }}><CircularProgress /><Typography color="text.secondary">Carregando registros...</Typography></Box></TableCell></TableRow>}
              {!carregandoLista && listaFiltrada.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">Nenhuma O.S. interna cadastrada.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      <Dialog
        open={resumoPendenciasAberto}
        onClose={() => setResumoPendenciasAberto(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Pendências das O.S.</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {osComPendencias.map(({ solicitacao, ensaiosPendentes }) => (
              <Box key={solicitacao.id}>
                <Typography fontWeight={800}>
                  O.S. {solicitacao.numeroOSFormatado || formatarNumeroOS(solicitacao.numeroOS)}
                </Typography>
                <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 3 }}>
                  {ensaiosPendentes.map((ensaio) => (
                    <Typography component="li" key={ensaio.id}>
                      {ensaio.nome || "Ensaio não informado"}
                    </Typography>
                  ))}
                </Box>
              </Box>
            ))}

            {osComPendencias.length === 0 && (
              <Typography>Nenhuma pendência.</Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResumoPendenciasAberto(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialog} onClose={fecharDialog} fullWidth maxWidth="md">
        <DialogTitle>{solicitacaoEditandoId ? "Editar O.S. interna" : "Nova O.S. interna"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            {solicitacaoEditandoId && (
              <Typography color="text.secondary">
                Na Caracterização, é possível marcar ou desmarcar ensaios já salvos. Para remover outros ensaios salvos, use o status Cancelado quando necessário.
              </Typography>
            )}

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Data"
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.data}
                onChange={(event) => setForm((atual) => ({ ...atual, data: event.target.value }))}
                sx={{ width: { md: 180 } }}
              />
              <Autocomplete
                options={clientes}
                inputValue={buscaCliente}
                onInputChange={(event, value) => setBuscaCliente(value)}
                filterOptions={(options) => {
                  const pesquisa = normalizarTexto(buscaCliente).trim();
                  if (!pesquisa) return options;

                  return options.filter((cliente) =>
                    normalizarTexto(cliente?.nome).includes(pesquisa)
                  );
                }}
                noOptionsText={<Button size="small" onMouseDown={() => history.push("/app/clientes?novo=1")}>+ Cadastrar cliente</Button>}
                getOptionLabel={(option) => option?.nome || ""}
                value={clienteSelecionado}
                onChange={(event, value) => {
                  setBuscaCliente(value?.nome || "");
                  setForm((atual) => ({
                    ...atual,
                    clienteId: value?.id || "",
                    cliente: value?.nome || "",
                    obraId: "",
                    obra: "",
                    materialId: "",
                    material: "",
                  }));
                }}
                renderInput={(params) => <TextField {...params} label="Cliente" size="small" />}
                fullWidth
              />
            </Stack>

            <Stack spacing={2}>
              <Autocomplete
                options={obrasFiltradas}
                noOptionsText={<Button size="small" onMouseDown={() => history.push("/app/cadastroobras?novo=1")}>+ Cadastrar obra</Button>}
                getOptionLabel={(option) => option?.nome || ""}
                value={obraSelecionada}
                onChange={(event, value) => selecionarObra(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Obra"
                    size="small"
                    helperText={
                      form.clienteId
                        ? "Obras filtradas pelo cliente selecionado."
                        : "Você pode buscar pela obra para preencher o cliente automaticamente."
                    }
                  />
                )}
                fullWidth
              />
            </Stack>

            <Stack spacing={2}>
              <Autocomplete
                options={materiaisFiltrados}
                getOptionLabel={(option) => labelMaterial(option)}
                value={materialSelecionado}
                onChange={(event, value) => selecionarMaterial(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Material"
                    size="small"
                    helperText={
                      mostrarTodosMateriais
                        ? "Mostrando todos os materiais cadastrados."
                        : form.obraId
                          ? "Materiais filtrados pela obra selecionada."
                          : form.clienteId
                            ? "Materiais filtrados pelo cliente selecionado."
                            : "Você pode buscar direto o material para preencher cliente e obra automaticamente."
                    }
                  />
                )}
                fullWidth
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={mostrarTodosMateriais}
                    onChange={(event) => setMostrarTodosMateriais(event.target.checked)}
                  />
                }
                label="Mostrar todos os materiais"
              />
            </Stack>

            {dadosBasicosPreenchidos && (
              <>
            <Box>
              <Typography fontWeight={800} mb={1}>Ensaios do laboratório</Typography>
              <Box className="solicitacao-servicos-checklist">
                {ensaiosLaboratorioDisponiveis.map((ensaio) => {
                  if (ensaio.subEnsaios?.length) {
                    const subEnsaiosPermitidos = ensaio.subEnsaios.filter((subEnsaio) =>
                      subEnsaioPermitidoParaMaterial(subEnsaio, materialSelecionado)
                    );
                    const grupoMarcado =
                      (form.ensaios || []).some((item) => item.id === ensaio.id) ||
                      ensaio.subEnsaios.some((subEnsaio) =>
                        (form.ensaios || []).some((item) => item.id === subEnsaio.id)
                      );
                    const todosMarcados =
                      subEnsaiosPermitidos.length > 0 &&
                      subEnsaiosPermitidos.every((subEnsaio) =>
                        (form.ensaios || []).some((item) => item.id === subEnsaio.id)
                      );

                    return (
                      <Box key={ensaio.id} className="solicitacao-servicos-grupo-ensaio">
                        <Box className="solicitacao-servicos-grupo-topo">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={grupoMarcado}
                                disabled={!materialSelecionado}
                                onChange={(event) => selecionarGrupoEnsaio(ensaio, event.target.checked)}
                              />
                            }
                            label={`${ensaio.nome} - ${ensaio.categoria}`}
                          />
                          {grupoMarcado && (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={!materialSelecionado}
                              onClick={() => selecionarTodosSubEnsaios(subEnsaiosPermitidos, !todosMarcados)}
                            >
                              {todosMarcados ? "Desmarcar todos" : "Marcar todos"}
                            </Button>
                          )}
                        </Box>

                        {grupoMarcado && (
                          <Box className="solicitacao-servicos-subensaios">
                            {ensaio.subEnsaios.map((subEnsaio) => (
                              <FormControlLabel
                                key={subEnsaio.id}
                                control={
                                  <Checkbox
                                    checked={(form.ensaios || []).some((item) => item.id === subEnsaio.id)}
                                    disabled={
                                      !materialSelecionado ||
                                      ensaioSalvoProtegido(subEnsaio.id) ||
                                      (!subEnsaioPermitidoParaMaterial(subEnsaio, materialSelecionado) &&
                                        !(form.ensaios || []).some((item) => item.id === subEnsaio.id))
                                    }
                                    onChange={(event) => selecionarEnsaio(subEnsaio, event.target.checked)}
                                  />
                                }
                                label={rotuloSubEnsaioParaMaterial(subEnsaio, materialSelecionado)}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    );
                  }

                  if (ehEnsaioRAA(ensaio)) {
  const ensaiosRAAForm = (form.ensaios || []).filter(ehEnsaioRAA);
  const possuiRAA = ensaiosRAAForm.length > 0;

  return (
    <Box key={ensaio.id} className="solicitacao-servicos-raa">
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <FormControlLabel
          control={
            <Checkbox
              checked={possuiRAA}
              disabled={!materialSelecionado}
              onChange={(event) => {
                if (event.target.checked) {
                  adicionarNovoRAA();
                  return;
                }

                setForm((atual) => ({
                  ...atual,
                  ensaios: (atual.ensaios || []).filter(
                    (item) => !ehEnsaioRAA(item) || ensaioJaSalvo(item.id)
                  ),
                }));
              }}
            />
          }
          label={`${ensaio.nome} - ${ensaio.categoria}`}
        />

        {possuiRAA && (
          <Button size="small" variant="outlined" onClick={adicionarNovoRAA}>
            + RAA
          </Button>
        )}
      </Stack>

      {ensaiosRAAForm.map((raaItem, index) => {
        const cimentoSelecionado =
          materiais.find((material) => material.id === raaItem?.cimentoId) || null;

        const adicaoSelecionada =
          materiais.find((material) => material.id === raaItem?.adicaoId) || null;

        return (
          <Box key={raaItem.id} className="solicitacao-servicos-raa-campos" sx={{ mt: 1.5 }}>
            <Typography fontWeight={700} fontSize={13}>
              RAA {index + 1}
              {ensaioJaSalvo(raaItem.id) ? " (salvo)" : ""}
            </Typography>

            <Autocomplete
              size="small"
              sx={{ gridColumn: "1 / -1" }}
              options={materiaisCimentoRAA}
              getOptionLabel={rotuloMaterialRAA}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={cimentoSelecionado}
              onChange={(event, value) =>
                atualizarDadosRAA(raaItem.id, {
                  cimento: value?.Descricao || value?.descricao || value?.nome || "",
                  cimentoCodigo: value?.codigo || value?.Codigo || "",
                  cimentoFornecedor: value?.Fornecedor || value?.fornecedor || "",
                  cimentoId: value?.id || "",
                  ...(value?.id !== raaItem.cimentoId
                    ? { status: "pendente", raaId: "", raaCodigo: "" }
                    : {}),
                })
              }
              renderInput={(params) => <TextField {...params} label="Cimento" />}
            />

            <Autocomplete
              size="small"
              sx={{ gridColumn: "1 / -1" }}
              options={materiaisAdicaoRAA}
              getOptionLabel={rotuloMaterialRAA}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={adicaoSelecionada}
              onChange={(event, value) =>
                atualizarDadosRAA(raaItem.id, {
                  adicao: value?.Descricao || value?.descricao || value?.nome || "",
                  adicaoId: value?.id || "",
                })
              }
              renderInput={(params) => <TextField {...params} label="Adição" />}
            />

            {raaItem.adicaoId && (
              <TextField
                size="small"
                label="Percentual de adição (%)"
                type="number"
                value={raaItem.percentualAdicao || ""}
                onChange={(event) =>
                  atualizarDadosRAA(raaItem.id, {
                    percentualAdicao: event.target.value,
                  })
                }
                inputProps={{
                  onWheel: (event) => event.target.blur(),
                }}
              />
            )}

            {!ensaioJaSalvo(raaItem.id) && (
              <Button
                size="small"
                color="error"
                variant="outlined"
                onClick={() => removerEnsaioForm(raaItem.id)}
              >
                Remover RAA
              </Button>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

                  return (
                    <FormControlLabel
                      key={ensaio.id}
                      control={
                        <Checkbox
                          checked={(form.ensaios || []).some((item) => item.id === ensaio.id)}
                          disabled={!materialSelecionado || ensaioJaSalvo(ensaio.id)}
                          onChange={(event) => selecionarEnsaio(ensaio, event.target.checked)}
                        />
                      }
                      label={`${ensaio.nome} - ${ensaio.categoria}`}
                    />
                  );
                })}
              </Box>
            </Box>

            <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
              <TextField
                label="Acrescentar ensaio"
                size="small"
                value={novoEnsaio}
                disabled={!materialSelecionado}
                onChange={(event) => setNovoEnsaio(event.target.value)}
                fullWidth
              />
              <Button
                variant="outlined"
                disabled={!materialSelecionado}
                onClick={adicionarEnsaioAvulso}
                sx={{ minWidth: 150 }}
              >
                Acrescentar
              </Button>
            </Stack>

            {ensaiosVisiveis(form.ensaios).length > 0 && (
              <Box className="solicitacao-servicos-selecionados">
                {ensaiosVisiveis(form.ensaios).map((ensaio) => (
                  <Chip
                    key={ensaio.id}
                    icon={<AssignmentIcon />}
                    label={ensaioJaSalvo(ensaio.id) ? `${ensaio.nome} (salvo)` : ensaio.nome}
                    onDelete={ensaioSalvoProtegido(ensaio.id) ? undefined : () => removerEnsaioForm(ensaio.id)}
                    deleteIcon={<DeleteIcon />}
                  />
                ))}
              </Box>
            )}
              </>
            )}

            <TextField
              label="Observações"
              size="small"
              value={form.observacoes}
              onChange={(event) => setForm((atual) => ({ ...atual, observacoes: event.target.value }))}
              multiline
              minRows={2}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharDialog}>Cancelar</Button>
          <Button variant="contained" onClick={salvarSolicitacao} disabled={salvando}>
            {salvando ? "Salvando..." : solicitacaoEditandoId ? "Salvar alteracoes" : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
